'use client';

import { FeedbackInviteLocale } from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from '@up2stay/up2promo-ui';
import { Eye, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { FeedbackFeatureGate } from '@/core/components/feedback/feedback-feature-gate';
import { FeedbackMailPreview } from '@/core/components/feedback/feedback-mail-preview';
import { FeedbackPageBreadcrumb } from '@/core/components/feedback/feedback-page-breadcrumb';
import {
  FEEDBACK_SECTION_CARD_CLASS,
  FEEDBACK_SECTION_CARD_CONTENT_CLASS,
} from '@/core/components/feedback/feedback-surface';
import { FeedbackVariableInserter } from '@/core/components/feedback/feedback-variable-inserter';
import { PageHeader } from '@/core/components/page-header';
import { ProfileAccentColorField } from '@/core/components/profile/profile-accent-color-field';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  useCreateFeedbackMailTemplate,
  useDeleteFeedbackMailTemplate,
  useFeedbackMailTemplate,
  useUpdateFeedbackMailTemplate,
  useFeedbackFeatureEnabled,
} from '@/core/hooks/use-feedback';
import { useFeedbackMailSample } from '@/core/hooks/use-feedback-mail-sample';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import {
  workspaceBrandAccent,
  workspaceBrandLogoUrl,
} from '@/core/lib/workspace-brand';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

export function FeedbackMessageEditorPage() {
  const dictRoot = useDictionary();
  const dict = dictRoot.feedback;
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const params = useParams<{ templateId?: string }>();
  const isNew = !params.templateId || params.templateId === 'new';
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const feedbackEnabled = useFeedbackFeatureEnabled();
  const mailSample = useFeedbackMailSample();

  const { data: existing } = useFeedbackMailTemplate(
    workspaceId,
    isNew ? undefined : params.templateId,
  );
  const createTemplate = useCreateFeedbackMailTemplate(workspaceId);
  const updateTemplate = useUpdateFeedbackMailTemplate(workspaceId);
  const deleteTemplate = useDeleteFeedbackMailTemplate(workspaceId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [locale, setLocale] = useState<FeedbackInviteLocale>(
    FeedbackInviteLocale.DE,
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [accentColor, setAccentColor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hydratedId, setHydratedId] = useState<string | null>(
    isNew ? '__new__' : null,
  );
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  if (!isNew && existing && hydratedId !== existing.id) {
    setHydratedId(existing.id);
    setName(existing.name);
    setDescription(existing.description ?? '');
    setLocale(existing.locale);
    setSubject(existing.subject);
    setBody(existing.body);
    setAccentColor(existing.accentColor ?? null);
  }

  const preview = (
    <FeedbackMailPreview
      subject={subject}
      body={body}
      sample={mailSample}
      fromLabel={dict.preview.from}
      toLabel={dict.preview.to}
      logoUrl={workspaceBrandLogoUrl(workspace?.photo)}
      accentColor={workspaceBrandAccent(accentColor)}
      ctaLabel={dict.preview.mailCta}
      poweredByLabel={dict.preview.poweredBy}
      className="min-h-[420px]"
    />
  );

  const insertIntoBody = (token: string) => {
    const el = bodyRef.current;
    if (!el) {
      setBody((prev) => `${prev}${token}`);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const onSave = async () => {
    if (!name.trim() || !subject.trim() || !body.trim()) {
      toast.error(dict.messages.editor.needFields);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        locale,
        subject: subject.trim(),
        body,
        accentColor,
        reminders: existing?.reminders,
      };
      if (isNew) {
        const created = await createTemplate.mutateAsync(payload);
        toast.success(dict.messages.editor.saveSuccess);
        if (created?.id) {
          navigateShell(shellPaths.feedbackMessageDetail(created.id));
        }
      } else {
        await updateTemplate.mutateAsync({
          templateId: params.templateId as string,
          body: payload,
        });
        toast.success(dict.messages.editor.saveSuccess);
      }
    } catch {
      toast.error(dict.messages.editor.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (isNew || !params.templateId) return;
    try {
      await deleteTemplate.mutateAsync(params.templateId);
      toast.success(dict.messages.editor.deleteSuccess);
      navigateShell(`${shellPaths.feedbackLibrary}?tab=messages`);
    } catch {
      toast.error(dict.messages.editor.deleteFailed);
    }
  };

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            title={
              isNew
                ? dict.messages.editor.createTitle
                : dict.messages.editor.editTitle
            }
            breadcrumb={
              <FeedbackPageBreadcrumb
                domainLabel={dictRoot.nav.navDomainWork}
                pageLabel={dictRoot.nav.feedbackLibrary}
              />
            }
            backHref={`${shellPaths.feedbackLibrary}?tab=messages`}
            actions={
              // Locked plan: nothing to save or preview (DEV-549).
              feedbackEnabled && (
                <div className="flex items-center gap-2">
                  {isMobile ? (
                    <Button
                      type="button"
                      variant="ghostAccent"
                      size="sm"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <Eye className="size-4" />
                      {dict.compose.preview}
                    </Button>
                  ) : null}
                  {!isNew ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void onDelete()}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    disabled={saving}
                    onClick={() => void onSave()}
                  >
                    {dict.messages.editor.save}
                  </Button>
                </div>
              )
            }
          />
          <FeedbackFeatureGate>
            <div className="grid gap-6 md:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.95fr)]">
              <Card className={cn('min-w-0', FEEDBACK_SECTION_CARD_CLASS)}>
                <CardContent
                  className={cn(
                    'space-y-5',
                    FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                    'pt-5 sm:pt-6',
                  )}
                >
                  <div className="space-y-2">
                    <Label>{dict.messages.editor.name}</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={dict.messages.editor.namePlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{dict.messages.editor.descriptionLabel}</Label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={dict.messages.editor.descriptionPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{dict.messages.editor.locale}</Label>
                    <Select
                      value={locale}
                      onValueChange={(v) =>
                        setLocale(v as FeedbackInviteLocale)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={FeedbackInviteLocale.DE}>
                          Deutsch
                        </SelectItem>
                        <SelectItem value={FeedbackInviteLocale.EN}>
                          English
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{dict.messages.editor.subject}</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={dict.messages.editor.subjectPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{dict.messages.editor.body}</Label>
                    <Textarea
                      ref={bodyRef}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder={dict.messages.editor.bodyPlaceholder}
                      className="min-h-48"
                    />
                    <FeedbackVariableInserter
                      hint={dict.messages.editor.variablesHint}
                      labels={{
                        contactName: dict.messages.editor.varContact,
                        locationName: dict.messages.editor.varLocation,
                        workspaceName: dict.messages.editor.varWorkspace,
                        feedbackUrl: dict.messages.editor.varFeedbackLink,
                        unsubscribeUrl: dict.messages.editor.varUnsubscribe,
                      }}
                      onInsert={insertIntoBody}
                    />
                  </div>
                  <ProfileAccentColorField
                    value={accentColor}
                    labels={{
                      label: dict.messages.editor.accentColor,
                      description: dict.messages.editor.accentColorDescription,
                      reset: dict.messages.editor.accentColorReset,
                      placeholder: dict.messages.editor.accentColorPlaceholder,
                    }}
                    onChange={setAccentColor}
                  />
                </CardContent>
              </Card>
              <aside className="hidden md:block">{preview}</aside>
            </div>
          </FeedbackFeatureGate>
        </AppPageLayout>
      </AppPageContent>

      <ResponsiveDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <ResponsiveDialogContent scrollable={false}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              {dict.compose.preview}
            </ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <div className="px-1 pb-4">{preview}</div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </AppIonPage>
  );
}
